import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Download, Search, Filter, CheckCircle2, AlertTriangle, Clock, FileText, Loader2, ExternalLink } from 'lucide-react';
import API from '../api';
import { useConfirm } from '../components/ConfirmDialog';

const STATI = {
  da_pagare: { label: 'Da pagare', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  pagato: { label: 'Pagato', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  parziale: { label: 'Parziale', cls: 'bg-sky-100 text-sky-700', icon: Clock },
  scaduto: { label: 'Scaduto', cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

const fmtEUR = (n) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('it-IT'); } catch { return s; } };

export default function FatturePassivePage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [selected, setSelected] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year, page, per_page: perPage });
      if (q) params.set('q', q);
      if (stato) params.set('stato', stato);
      const { data } = await API.get(`/api/sdi/fatture-passive?${params}`);
      setItems(data.items || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
    } catch (e) {
      alert('Errore caricamento: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [year, q, stato, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/api/cantieri').then(({ data }) => {
      setCantieri(Array.isArray(data) ? data : (data.items || []));
    }).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`/api/sdi/fatture-passive/sync?year=${year}`);
      let msg = `Sync completato: ${data.inserted} nuove, ${data.updated} aggiornate`;
      if (data.errors?.length) msg += `\nWarning: ${data.errors.join('; ')}`;
      alert(msg);
      await load();
    } catch (e) {
      alert('Errore sync: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const handlePatch = async (id, updates) => {
    try {
      const { data } = await API.patch(`/api/sdi/fatture-passive/${id}`, updates);
      setItems(items.map(i => i.id === id ? data : i));
      if (selected?.id === id) setSelected(data);
    } catch (e) {
      alert('Errore aggiornamento: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDownloadXml = async (id) => {
    try {
      const { data } = await API.get(`/api/sdi/fatture-passive/${id}/xml`);
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      } else if (data.e_invoice) {
        alert('XML disponibile inline. Dati: ' + JSON.stringify(data.e_invoice).slice(0, 500));
      }
    } catch (e) {
      alert('XML non disponibile: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const StatoBadge = ({ s }) => {
    const cfg = STATI[s] || STATI.da_pagare;
    const Icon = cfg.icon;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${cfg.cls}`}><Icon size={12} />{cfg.label}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-5" data-testid="fatture-passive-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fatture Passive</h1>
          <p className="text-sm text-slate-500">Fatture ricevute dai fornitori via SDI (sincronizzate da FattureInCloud)</p>
        </div>
        <button onClick={handleSync} disabled={syncing} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-bold hover:bg-primary-dark disabled:opacity-60" data-testid="sync-btn">
          {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          {syncing ? 'Sincronizzazione...' : `Sincronizza ${year}`}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['da_pagare', 'pagato', 'parziale', 'scaduto'].map(k => {
          const cfg = STATI[k]; const s = stats[k] || { count: 0, tot: 0 }; const Icon = cfg.icon;
          return (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs"><Icon size={14} /> {cfg.label}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{s.count}</div>
              <div className="text-xs text-slate-500">{fmtEUR(s.tot)}</div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Cerca per fornitore, P.IVA, numero, descrizione..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm"
          />
        </div>
        <select value={year} onChange={e => { setYear(parseInt(e.target.value)); setPage(1); }} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          {[0, 1, 2, 3].map(o => { const y = new Date().getFullYear() - o; return <option key={y} value={y}>{y}</option>; })}
        </select>
        <select value={stato} onChange={e => { setStato(e.target.value); setPage(1); }} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <FileText size={32} className="mx-auto mb-2 opacity-30" />
            <div>Nessuna fattura passiva per {year}.</div>
            <div className="text-xs mt-1">Clicca <b>Sincronizza {year}</b> per scaricarle da FattureInCloud.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Numero</th>
                <th className="px-3 py-2 text-left">Fornitore</th>
                <th className="px-3 py-2 text-right">Imponibile</th>
                <th className="px-3 py-2 text-right">IVA</th>
                <th className="px-3 py-2 text-right">Totale</th>
                <th className="px-3 py-2 text-center">Stato</th>
                <th className="px-3 py-2 text-left">Cantiere</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(f.data)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{f.numero || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{f.fornitore_nome || '—'}</div>
                    {f.fornitore_piva && <div className="text-xs text-slate-500">P.IVA {f.fornitore_piva}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(f.importo_netto)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtEUR(f.importo_iva)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtEUR(f.importo_totale)}</td>
                  <td className="px-3 py-2 text-center"><StatoBadge s={f.stato_pagamento} /></td>
                  <td className="px-3 py-2">
                    <select
                      value={f.cantiere_id || ''}
                      onChange={e => handlePatch(f.id, { cantiere_id: e.target.value || null })}
                      className="text-xs px-2 py-1 border border-slate-200 rounded max-w-[160px]"
                    >
                      <option value="">— nessuno —</option>
                      {cantieri.map(c => <option key={c.id} value={c.id}>{c.nome || c.titolo}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelected(f)} className="text-primary hover:underline text-xs font-semibold mr-2">Dettagli</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div>Totale: {total} fatture</div>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50">Prev</button>
            <span className="px-3 py-1">Pagina {page} di {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3">
          <div className="bg-white rounded-xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <div className="font-bold text-lg">Fattura {selected.numero || '—'}</div>
                <div className="text-sm text-slate-500">{selected.fornitore_nome} · {fmtDate(selected.data)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 text-2xl">×</button>
            </div>
            <div className="p-4 overflow-auto space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">P.IVA</div><div>{selected.fornitore_piva || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Codice Fiscale</div><div>{selected.fornitore_cf || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Imponibile</div><div className="font-semibold">{fmtEUR(selected.importo_netto)}</div></div>
                <div><div className="text-xs text-slate-500">IVA</div><div>{fmtEUR(selected.importo_iva)}</div></div>
                <div className="col-span-2"><div className="text-xs text-slate-500">Totale</div><div className="font-bold text-lg">{fmtEUR(selected.importo_totale)}</div></div>
                {selected.descrizione && <div className="col-span-2"><div className="text-xs text-slate-500">Descrizione</div><div>{selected.descrizione}</div></div>}
              </div>

              <div className="border-t pt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Stato pagamento</label>
                    <select value={selected.stato_pagamento} onChange={e => handlePatch(selected.id, { stato_pagamento: e.target.value })} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
                      {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Data pagamento</label>
                    <input type="date" value={selected.data_pagamento || ''} onChange={e => handlePatch(selected.id, { data_pagamento: e.target.value })} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Metodo pagamento</label>
                    <input type="text" value={selected.metodo_pagamento || ''} onChange={e => handlePatch(selected.id, { metodo_pagamento: e.target.value })} placeholder="Bonifico, RiBa, Contanti..." className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Cantiere</label>
                    <select value={selected.cantiere_id || ''} onChange={e => handlePatch(selected.id, { cantiere_id: e.target.value || null })} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
                      <option value="">— nessuno —</option>
                      {cantieri.map(c => <option key={c.id} value={c.id}>{c.nome || c.titolo}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Note interne</label>
                  <textarea value={selected.note_interne || ''} onChange={e => handlePatch(selected.id, { note_interne: e.target.value })} rows={2} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => handleDownloadXml(selected.id)} className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">
                <Download size={14} /> Scarica XML
              </button>
              <button onClick={() => setSelected(null)} className="px-4 py-2 bg-primary text-white rounded text-sm font-semibold">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
